import React, { useMemo } from 'react';
import {
  CLUB_COURT_COUNT_BANDS,
  CLUB_LOCATION_COUNT_BANDS,
  CLUB_OFFERINGS,
  RACQUET_SPORT_IDS,
  RACQUET_SPORT_LABELS,
  recommendClubPlan,
  type ClubCourtCountBand,
  type ClubLocationCountBand,
  type ClubOffering,
  type ClubOperatingProfileInput,
  type ClubPlanTier,
  type RacquetSportId,
} from '@court-sync/shared';
import { Input, Field, FieldLabel } from '@/components/ui';
import { SportIconTile } from '@/components/sport/SportIcons';
import { SelectableCard, SelectableCardGrid } from './SelectableCard';
import { ClubOnboardingPlanSelection } from './ClubOnboardingPlanSelection';

export const CLUB_QUESTIONNAIRE_STEP_IDS = [
  'club-sports',
  'club-offerings',
  'club-courts',
  'club-locations',
  'plan',
] as const;

export type ClubQuestionnaireStepId = (typeof CLUB_QUESTIONNAIRE_STEP_IDS)[number];

export const CLUB_QUESTIONNAIRE_WIZARD_STEPS = [
  {
    id: 'club-sports',
    title: 'Which sports does your club offer?',
    description: 'Select all that apply. Choose one as your primary sport.',
  },
  {
    id: 'club-offerings',
    title: 'What do you offer members and visitors?',
    description: 'Select everything you plan to manage in CourtSync. Feature choices help us recommend Club or Club Pro.',
  },
  {
    id: 'club-courts',
    title: 'How many courts do you have?',
    description: 'An approximate count is fine.',
  },
  {
    id: 'club-locations',
    title: 'How many locations?',
    description: 'Physical venues where your courts are located.',
  },
  {
    id: 'plan',
    title: 'Choose your club plan',
    description: 'Compare Club and Club Pro — change anytime.',
  },
] as const;

export function resolveClubQuestionnaireWizardSteps(options?: {
  skipSports?: boolean;
  skipLocations?: boolean;
}) {
  return CLUB_QUESTIONNAIRE_WIZARD_STEPS.filter((step) => {
    if (options?.skipSports && step.id === 'club-sports') return false;
    if (options?.skipLocations && step.id === 'club-locations') return false;
    return true;
  });
}

const OFFERING_LABELS: Record<ClubOffering, string> = {
  court_hire: 'Court hire',
  memberships: 'Memberships',
  inventory_hire: 'Equipment hire',
  academy_court_access: 'Academy court access',
};

const COURT_BAND_LABELS: Record<ClubCourtCountBand, string> = {
  '1-4': '1–4 courts',
  '5-12': '5–12 courts',
  '13+': '13+ courts',
};

const LOCATION_BAND_LABELS: Record<ClubLocationCountBand, string> = {
  '1': '1 location',
  '2-3': '2–3 locations',
  '4+': '4+ locations',
};

export function isClubQuestionnaireStepComplete(
  stepId: ClubQuestionnaireStepId,
  profile: Partial<ClubOperatingProfileInput>
): boolean {
  switch (stepId) {
    case 'club-sports': {
      const sports = profile.sports ?? [];
      if (sports.length === 0) return false;
      if (sports.filter((s) => s.isPrimary).length !== 1) return false;
      const other = sports.find((s) => s.sportId === 'other');
      if (other && !other.customLabel?.trim()) return false;
      return true;
    }
    case 'club-offerings':
      return (profile.offerings?.length ?? 0) > 0;
    case 'club-courts':
      return Boolean(profile.courtCountBand);
    case 'club-locations':
      return Boolean(profile.locationCountBand);
    case 'plan':
      return true;
    default:
      return false;
  }
}

function toggleSport(
  profile: Partial<ClubOperatingProfileInput>,
  sportId: RacquetSportId
): Partial<ClubOperatingProfileInput> {
  const sports = [...(profile.sports ?? [])];
  const existingIndex = sports.findIndex((s) => s.sportId === sportId);
  if (existingIndex >= 0) {
    sports.splice(existingIndex, 1);
    if (sports.length > 0 && !sports.some((s) => s.isPrimary)) {
      sports[0] = { ...sports[0], isPrimary: true };
    }
    return { ...profile, sports };
  }
  const isFirst = sports.length === 0;
  sports.push({ sportId, isPrimary: isFirst });
  return { ...profile, sports };
}

function setPrimarySport(
  profile: Partial<ClubOperatingProfileInput>,
  sportId: RacquetSportId
): Partial<ClubOperatingProfileInput> {
  const sports = (profile.sports ?? []).map((s) => ({
    ...s,
    isPrimary: s.sportId === sportId,
  }));
  return { ...profile, sports };
}

function toggleOffering(
  profile: Partial<ClubOperatingProfileInput>,
  offering: ClubOffering
): Partial<ClubOperatingProfileInput> {
  const current = profile.offerings ?? [];
  const offerings = current.includes(offering)
    ? current.filter((o) => o !== offering)
    : [...current, offering];
  return { ...profile, offerings };
}

export interface ClubNeedsQuestionnaireProps {
  stepId: ClubQuestionnaireStepId;
  profile: Partial<ClubOperatingProfileInput>;
  onProfileChange: (profile: Partial<ClubOperatingProfileInput>) => void;
  selectedClubPlanTier?: ClubPlanTier;
  onSelectClubPlanTier?: (tier: ClubPlanTier) => void;
  /** @deprecated use selectedClubPlanTier */
  selectedPlanTier?: ClubPlanTier;
  /** @deprecated use onSelectClubPlanTier */
  onSelectPlanTier?: (tier: ClubPlanTier) => void;
  marketCountry?: string | null;
}

export function ClubNeedsQuestionnaire({
  stepId,
  profile,
  onProfileChange,
  selectedClubPlanTier,
  onSelectClubPlanTier,
  selectedPlanTier,
  onSelectPlanTier,
  marketCountry,
}: ClubNeedsQuestionnaireProps) {
  const effectiveClubTier = selectedClubPlanTier ?? selectedPlanTier ?? 'starter';
  const handleSelectClubTier = onSelectClubPlanTier ?? onSelectPlanTier;

  const recommendation = useMemo(() => recommendClubPlan(profile), [profile]);

  if (stepId === 'plan') {
    return (
      <ClubOnboardingPlanSelection
        recommendation={recommendation}
        selectedClubPlanTier={effectiveClubTier}
        onSelectClubPlanTier={handleSelectClubTier}
        marketCountry={marketCountry}
      />
    );
  }

  if (stepId === 'club-sports') {
    const sports = profile.sports ?? [];
    return (
      <div className="space-y-4">
        <SelectableCardGrid label="Club sports">
          {RACQUET_SPORT_IDS.map((sportId) => {
            const selected = sports.some((s) => s.sportId === sportId);
            const isPrimary = sports.find((s) => s.sportId === sportId)?.isPrimary;
            return (
              <SelectableCard
                key={sportId}
                id={`club-sport-${sportId}`}
                title={RACQUET_SPORT_LABELS[sportId]}
                description={isPrimary ? 'Primary sport' : selected ? 'Tap star to set primary' : undefined}
                icon={<SportIconTile sportId={sportId} className="h-8 w-8" />}
                selected={selected}
                onSelect={() => onProfileChange(toggleSport(profile, sportId))}
                badge={isPrimary ? 'Primary' : undefined}
              />
            );
          })}
        </SelectableCardGrid>
        {sports.length > 1 ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Primary sport</p>
            <div className="flex flex-wrap gap-2">
              {sports.map((s) => (
                <button
                  key={s.sportId}
                  type="button"
                  className={`rounded-full px-3 py-1 text-sm border ${
                    s.isPrimary
                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-950/40'
                      : 'border-slate-200 dark:border-slate-700'
                  }`}
                  onClick={() => onProfileChange(setPrimarySport(profile, s.sportId))}
                >
                  {RACQUET_SPORT_LABELS[s.sportId]}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {sports.some((s) => s.sportId === 'other') ? (
          <Field>
            <FieldLabel htmlFor="club-other-sport-label">Other sport label</FieldLabel>
            <Input
              id="club-other-sport-label"
              value={sports.find((s) => s.sportId === 'other')?.customLabel ?? ''}
              onChange={(event) => {
                const nextSports = sports.map((s) =>
                  s.sportId === 'other' ? { ...s, customLabel: event.target.value } : s
                );
                onProfileChange({ ...profile, sports: nextSports });
              }}
              placeholder="e.g. Platform tennis"
            />
          </Field>
        ) : null}
      </div>
    );
  }

  if (stepId === 'club-offerings') {
    return (
      <SelectableCardGrid label="Club offerings" className="sm:grid-cols-1" selectionMode="multiple">
        {CLUB_OFFERINGS.map((offering) => (
          <SelectableCard
            key={offering}
            id={`club-offering-${offering}`}
            title={OFFERING_LABELS[offering]}
            selected={(profile.offerings ?? []).includes(offering)}
            onSelect={() => onProfileChange(toggleOffering(profile, offering))}
            selectionMode="multiple"
          />
        ))}
        <SelectableCard
          id="club-needs-payments"
          title="Online card payments"
          description="Accept payments for court hire and memberships."
          selected={profile.needsOnlinePayments === true}
          onSelect={() =>
            onProfileChange({
              ...profile,
              needsOnlinePayments: !profile.needsOnlinePayments,
            })
          }
          selectionMode="multiple"
        />
        <SelectableCard
          id="club-needs-subdomain"
          title="Branded subdomain"
          description="yourclub.courtsync.io for your public club site."
          selected={profile.needsBrandedSubdomain === true}
          onSelect={() =>
            onProfileChange({
              ...profile,
              needsBrandedSubdomain: !profile.needsBrandedSubdomain,
            })
          }
          selectionMode="multiple"
        />
      </SelectableCardGrid>
    );
  }

  if (stepId === 'club-courts') {
    return (
      <SelectableCardGrid label="Court count" className="sm:grid-cols-1">
        {CLUB_COURT_COUNT_BANDS.map((band) => (
          <SelectableCard
            key={band}
            id={`club-courts-${band}`}
            title={COURT_BAND_LABELS[band]}
            selected={profile.courtCountBand === band}
            onSelect={() => onProfileChange({ ...profile, courtCountBand: band })}
          />
        ))}
      </SelectableCardGrid>
    );
  }

  if (stepId === 'club-locations') {
    return (
      <SelectableCardGrid label="Location count" className="sm:grid-cols-1">
        {CLUB_LOCATION_COUNT_BANDS.map((band) => (
          <SelectableCard
            key={band}
            id={`club-locations-${band}`}
            title={LOCATION_BAND_LABELS[band]}
            selected={profile.locationCountBand === band}
            onSelect={() => onProfileChange({ ...profile, locationCountBand: band })}
          />
        ))}
      </SelectableCardGrid>
    );
  }

  return null;
}
